/**
 * « Les Mots Manquants » façon karaoké : une musique (YouTube ou fichier), les paroles qui défilent,
 * et la musique qui se coupe sur chaque trou. L'animateur tape ce que dit le candidat, valide, puis relance.
 * Données : { media, title, start, lines: [{ text, t, stop }] } — les mots à trouver sont entre [crochets].
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { media, youTubeId } from './quizShowFormats';

const HOLE_PTS = [2, 3, 4, 5, 6];
export const holePts = (k) => HOLE_PTS[Math.min(k, HOLE_PTS.length - 1)];

// « Je serai [le meilleur] dresseur » → { before, hole, after }
export const splitHole = (text) => {
  const m = /^(.*?)\[([^\]]+)\](.*)$/.exec(String(text || ''));
  return m ? { before: m[1], hole: m[2].trim(), after: m[3] } : null;
};
const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[’']/g, ' ').replace(/[^a-z0-9\s-]/g, ' ').replace(/\s+/g, ' ').trim();
export const fmtT = (t) => (Number.isFinite(t) ? `${Math.floor(t / 60)}:${(t % 60).toFixed(1).padStart(4, '0')}` : '—');
export const parseT = (s) => { const m = /^(\d+):(\d+(?:[.,]\d+)?)$/.exec(String(s).trim()); return m ? Number(m[1]) * 60 + Number(m[2].replace(',', '.')) : Number(String(s).replace(',', '.')); };

// Moment de coupure d'un trou : celui saisi, sinon estimé d'après la place des mots dans la ligne
// (entre le début de cette ligne et celui de la suivante), pour ne taper qu'une fois par ligne.
export const holeStop = (lines, k) => {
  const l = lines[k]; const h = splitHole(l && l.text); const t = Number(l && l.t) || 0;
  if (!h) return t;
  if (Number(l.stop) > t) return Number(l.stop);
  const nextT = lines.slice(k + 1).map((x) => Number(x.t)).find((x) => x > t);
  const len = (h.before + h.hole + h.after).length || 1;
  const span = nextT ? nextT - t : len * 0.08;
  return Math.round((t + Math.max(0.2, span * (h.before.length / len) - 0.15)) * 10) / 10;
};
// Paroles au format LRC (« [01:02.50] texte ») → lignes synchronisées
export const parseLRC = (txt) => {
  const out = [];
  String(txt || '').split(/\r?\n/).forEach((row) => {
    const stamps = [...row.matchAll(/\[(\d+):(\d+(?:[.,]\d+)?)\]/g)];
    if (!stamps.length) return;
    const text = row.replace(/\[\d+:\d+(?:[.,]\d+)?\]/g, '').trim();
    stamps.forEach((m) => out.push({ text, t: Math.round((Number(m[1]) * 60 + Number(m[2].replace(',', '.'))) * 10) / 10, stop: '' }));
  });
  return out.filter((l) => l.text).sort((a, b) => a.t - b.t);
};

// ——— Lecteur commun (YouTube caché ou fichier audio) ———
let ytLoad = null;
const loadYT = () => {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (!ytLoad) ytLoad = new Promise((res) => { const prev = window.onYouTubeIframeAPIReady; window.onYouTubeIframeAPIReady = () => { if (prev) prev(); res(); }; const s = document.createElement('script'); s.src = 'https://www.youtube.com/iframe_api'; document.head.appendChild(s); });
  return ytLoad;
};
export function useMediaPlayer(src) {
  const vid = youTubeId(src);
  const box = useRef(null); const yt = useRef(null); const audio = useRef(null);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [ended, setEnded] = useState(false);
  useEffect(() => {
    setReady(false); setPlaying(false); setEnded(false); setTime(0);
    if (!src) return undefined;
    let dead = false;
    if (vid) {
      loadYT().then(() => {
        if (dead || !box.current) return;
        const el = document.createElement('div'); box.current.innerHTML = ''; box.current.appendChild(el);
        yt.current = new window.YT.Player(el, { width: 200, height: 200, videoId: vid, playerVars: { controls: 0, disablekb: 1, playsinline: 1, rel: 0 },
          events: { onReady: () => !dead && setReady(true), onStateChange: (e) => { if (e.data === 0) { setEnded(true); setPlaying(false); } } } });
      });
    } else {
      const a = new Audio(media(src)); a.preload = 'auto'; audio.current = a;
      a.oncanplay = () => !dead && setReady(true);
      a.onended = () => { setEnded(true); setPlaying(false); };
    }
    return () => { dead = true; if (yt.current && yt.current.destroy) yt.current.destroy(); yt.current = null; if (audio.current) { audio.current.pause(); audio.current = null; } };
  }, [src, vid]);
  const getTime = useCallback(() => (yt.current && yt.current.getCurrentTime ? yt.current.getCurrentTime() : audio.current ? audio.current.currentTime : 0), []);
  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => setTime(getTime()), 60);
    return () => clearInterval(id);
  }, [playing, getTime]);
  const play = useCallback(() => { setEnded(false); if (yt.current && yt.current.playVideo) yt.current.playVideo(); else if (audio.current) audio.current.play().catch(() => {}); setPlaying(true); }, []);
  const pause = useCallback(() => { if (yt.current && yt.current.pauseVideo) yt.current.pauseVideo(); else if (audio.current) audio.current.pause(); setPlaying(false); setTime(getTime()); }, [getTime]);
  const seek = useCallback((t) => { if (yt.current && yt.current.seekTo) yt.current.seekTo(t, true); else if (audio.current) audio.current.currentTime = t; setTime(t); }, []);
  const node = <div className="qs-yt-hidden" ref={box} aria-hidden="true" />;
  return { node, time, playing, ready: ready || !src, ended, play, pause, seek, getTime };
}

// ——— L'épreuve ———
// Temps de début fiables : une ligne sans temps (ou en arrière) est placée entre ses voisines,
// sinon elle « attirait » le surlignage et le texte démarrait au milieu de la chanson.
const fixTimes = (raw) => {
  const known = raw.map((l, k) => { const t = Number(l.t); return l.t !== '' && l.t != null && Number.isFinite(t) && (t > 0 || k === 0) ? t : null; });
  let last = -Infinity;
  known.forEach((t, k) => { if (t != null) { if (t < last) known[k] = null; else last = t; } });
  return known.map((t, k) => {
    if (t != null) return t;
    let a = k - 1; while (a >= 0 && known[a] == null) a -= 1;
    let b = k + 1; while (b < known.length && known[b] == null) b += 1;
    const ta = a >= 0 ? known[a] : 0;
    if (b < known.length) return ta + ((known[b] - ta) * (k - a)) / (b - a);
    return ta + 3 * (k - a);
  });
};
// « l'aventure » → cases : l ' a v e n t u r e (les lettres sont à trouver, la ponctuation est donnée)
const isLetter = (c) => /[0-9A-Za-zÀ-ÖØ-öø-ÿŒœ]/.test(c);
const letterCount = (w) => [...w].filter(isLetter).length;
// Répartit les lettres tapées dans les cases, mot par mot (espaces facultatifs, ponctuation déjà posée)
const fillWords = (words, typed) => {
  const letters = [...String(typed || '')].filter(isLetter); let n = 0;
  return words.map((w) => [...w].map((c) => (isLetter(c) ? letters[n++] || '' : c)));
};
function HoleSlots({ words, showIni, showWord, typed, typing }) {
  const filled = typed != null ? fillWords(words, typed) : null;
  const total = words.reduce((a, w) => a + letterCount(w), 0);
  const nextIdx = typed != null ? Math.min([...String(typed)].filter(isLetter).length, total) : -1;
  let idx = 0;
  return words.map((w, j) => (
    <em key={j} className="qs-kara-word">
      {[...w].map((c, n) => {
        if (!isLetter(c)) return <span key={n} className="qs-kara-punct">{c}</span>;
        const me = idx++;
        const first = [...w].findIndex(isLetter) === n;
        const hint = showWord === j || (showIni && first);
        const mine = filled ? filled[j][n] : '';
        return <span key={n} className={`qs-kara-slot${mine ? ' is-typed' : hint ? ' is-shown' : ''}${typing && me === nextIdx ? ' is-caret' : ''}`}>{mine ? mine.toUpperCase() : hint ? c.toUpperCase() : ''}</span>;
      })}
      <sup>{letterCount(w)}</sup>
    </em>
  ));
}

export function KaraokeRound({ data, onDone, cheat, sfx, Bar, Done }) {
  const lines = useMemo(() => { const raw = (data.lines || []).filter((l) => l.text); const ts = fixTimes(raw); return raw.map((l, k) => ({ ...l, t: ts[k], h: splitHole(l.text) })); }, [data]);
  const holes = useMemo(() => lines.map((l, k) => (l.h ? { k, stop: holeStop(lines, k), words: l.h.hole } : null)).filter(Boolean), [lines]);
  const p = useMediaPlayer(data.media);
  const [hi, setHi] = useState(0); // trou en cours
  const [phase, setPhase] = useState('ready'); // ready | playing | hole | judged | end
  const [typed, setTyped] = useState('');
  const [focused, setFocused] = useState(false);
  const [verdict, setVerdict] = useState(null); // { ok, marks }
  const [results, setResults] = useState({}); // k -> { ok, typed }
  const [jokers, setJokers] = useState({ ini: null, mot: null }); // index du trou où chaque joker a servi
  const input = useRef(null);
  const total = Object.entries(results).reduce((s, [k, r]) => s + (r.ok ? holePts(holes.findIndex((h) => h.k === Number(k))) : 0), 0);
  const maxPts = holes.reduce((s, _, k) => s + holePts(k), 0);
  const hole = holes[hi];

  useEffect(() => { cheat(hole ? [`Trou ${hi + 1} : « ${hole.words} »`] : ['Plus de trou']); }, [hi, hole && hole.words]); // eslint-disable-line
  // La musique se coupe pile sur le trou
  useEffect(() => {
    if (phase !== 'playing' || !hole) return;
    if (p.time >= hole.stop - 0.05) { p.pause(); p.seek(hole.stop); setPhase('hole'); setTyped(''); setVerdict(null); sfx('buzz'); setTimeout(() => input.current && input.current.focus(), 50); }
  }, [p.time, phase, hole]); // eslint-disable-line
  useEffect(() => { if (p.ended && phase === 'playing') setPhase('end'); }, [p.ended, phase]);
  // Plus de trou : on laisse finir la dernière ligne puis on conclut
  useEffect(() => {
    if (phase !== 'playing' || hole) return undefined;
    const lastT = lines.length ? lines[lines.length - 1].t : 0;
    if (p.time > lastT + 6) { p.pause(); setPhase('end'); }
    return undefined;
  }, [p.time, phase, hole, lines]); // eslint-disable-line

  const cur = (() => { let c = -1; lines.forEach((l, k) => { if (p.time + 0.05 >= l.t) c = k; }); return phase === 'hole' || phase === 'judged' ? hole ? hole.k : c : c; })();
  const start = () => { sfx('start'); p.seek(Number(data.start) || 0); p.play(); setPhase('playing'); };
  const judge = () => {
    const guess = fillWords(hole.words.split(/\s+/), typed).map((w) => w.join('')).join(' ');
    const want = norm(hole.words).split(' '); const got = norm(guess).split(' ');
    const marks = want.map((w, k) => got[k] === w);
    const ok = marks.every(Boolean) && got.length === want.length;
    setVerdict({ ok, marks }); setResults((r) => ({ ...r, [hole.k]: { ok, typed } })); setPhase('judged'); sfx(ok ? 'win' : 'bad');
  };
  const force = (ok) => { setVerdict((v) => ({ ...v, ok, forced: true })); setResults((r) => ({ ...r, [hole.k]: { ...r[hole.k], ok } })); sfx(ok ? 'good' : 'bad'); };
  const resume = () => { setHi(hi + 1); setVerdict(null); setPhase('playing'); p.play(); };
  const replayLine = () => { if (!hole) return; const l = lines[hole.k]; p.seek(Math.max(0, (lines[hole.k - 1] ? lines[hole.k - 1].t : l.t - 3))); setPhase('playing'); p.play(); };

  const view = (cur < 0 ? [0, 1, 2] : [cur - 1, cur, cur + 1, cur + 2]).filter((k) => k >= 0 && k < lines.length);
  const holeWords = hole ? hole.words.split(/\s+/) : [];
  const iniHere = jokers.ini === hi; const motHere = jokers.mot === hi;
  // Joker Mot : dévoile le mot le plus long du trou (seulement si le trou compte au moins deux mots)
  const motIdx = holeWords.length > 1 ? holeWords.reduce((m, w, j) => (letterCount(w) > letterCount(holeWords[m]) ? j : m), 0) : -1;
  const useIni = () => { if (jokers.ini != null) return; setJokers((j) => ({ ...j, ini: hi })); sfx('flip'); setTimeout(() => input.current && input.current.focus(), 0); };
  const useMot = () => { if (jokers.mot != null || holeWords.length < 2) return; setJokers((j) => ({ ...j, mot: hi })); sfx('flip'); setTimeout(() => input.current && input.current.focus(), 0); };
  const unsynced = lines.length > 1 && lines.every((l) => !Number(l.t));
  if (lines.length && data.media && unsynced) return <div className="qs-round"><Bar label="Les Mots Manquants" points={0} of={20} /><p className="qs-explain">Cette chanson n’est pas encore synchronisée : dans l’éditeur, importe les paroles synchronisées ou clique sur « Synchroniser au tap » et tape Espace au début de chaque ligne.</p><Done points={0} onDone={onDone} label="Terminer quand même" /></div>;
  if (!lines.length || !data.media) return <div className="qs-round"><Bar label="Les Mots Manquants" points={0} of={20} /><p className="qs-explain">Il manque la musique ou les paroles : complète cette épreuve dans l’éditeur.</p><Done points={0} onDone={onDone} label="Terminer quand même" /></div>;
  return (
    <div className="qs-round qs-karaoke">
      {p.node}
      <Bar label={`${data.title || 'Les Mots Manquants'} · trou ${Math.min(hi + 1, holes.length)} / ${holes.length}${hole ? ` · ${holePts(hi)} pts` : ''}`} points={total} of={maxPts} step={hi} steps={holes.length} />
      <div className={`qs-kara-stage is-${phase}`}>
        <div className="qs-kara-eq">{Array.from({ length: 16 }, (_, k) => <i key={k} style={{ animationDelay: `${-k * 0.11}s` }} />)}</div>
        {phase === 'ready' && <div className="qs-kara-start"><b>{data.title}</b><button type="button" className="qs-btn is-gold is-big" disabled={!p.ready} onClick={start}>{p.ready ? '▶ Lancer la musique' : 'Chargement…'}</button></div>}
        {phase !== 'ready' && (
          <div className="qs-kara-lines">
            {view.map((k) => {
              const l = lines[k]; const isCur = k === cur; const r = results[k];
              const isHole = hole && k === hole.k && (phase === 'hole' || phase === 'judged');
              return (
                <p key={k} className={`qs-kara-line${isCur ? ' is-cur' : k < cur ? ' is-past' : ''}`}>
                  {l.h ? (
                    <>
                      {l.h.before}
                      <span className={`qs-kara-hole${isHole ? ' is-open' : ''}${r ? (r.ok ? ' is-good' : ' is-bad') : ''}`}>
                        {isHole && phase === 'hole' ? <span className="qs-kara-type" onClick={() => input.current && input.current.focus()}><HoleSlots words={l.h.hole.split(/\s+/)} showIni={iniHere} showWord={motHere ? motIdx : -1} typed={typed} typing={focused} /></span> : isHole && verdict ? l.h.hole.split(/\s+/).map((w, j) => <em key={j} className={verdict.forced ? (verdict.ok ? 'ok' : 'ko') : verdict.marks[j] ? 'ok' : 'ko'}>{w}</em>) : r ? l.h.hole : <HoleSlots words={l.h.hole.split(/\s+/)} />}
                      </span>
                      {(r || k < cur) ? l.h.after : ''}
                    </>
                  ) : l.text}
                </p>
              );
            })}
          </div>
        )}
      </div>
      {phase === 'hole' && (
        <form className="qs-host qs-kara-input" onSubmit={(e) => { e.preventDefault(); judge(); }}>
          <span className="qs-host-tip">{holeWords.length} mot{holeWords.length > 1 ? 's' : ''} ({holeWords.map(letterCount).join(' + ')} lettres) · écris directement dans les cases (Entrée pour valider)</span>
          <input ref={input} className="qs-kara-ghost" value={typed} onChange={(e) => setTyped(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck="false" aria-label="Réponse du candidat" />
          <button type="submit" className="qs-btn is-gold">Valider</button>
          <button type="button" className="qs-btn is-ghost" onClick={replayLine}>↺ Réécouter</button>
          <span className="qs-kara-jokers">
            <button type="button" className={`qs-btn is-small${iniHere ? ' is-armed' : ''}`} disabled={jokers.ini != null && !iniHere} onClick={useIni} title="Dévoile la première lettre de chaque mot (une fois par partie)">🔤 Joker Initiales</button>
            <button type="button" className={`qs-btn is-small${motHere ? ' is-armed' : ''}`} disabled={(jokers.mot != null && !motHere) || holeWords.length < 2} onClick={useMot} title={holeWords.length < 2 ? 'Utilisable sur un trou d’au moins deux mots' : 'Dévoile un mot entier (une fois par partie)'}>💬 Joker Mot</button>
          </span>
        </form>
      )}
      {phase === 'judged' && (
        <div className="qs-host">
          <span className={`qs-kara-verdict${verdict.ok ? ' ok' : ''}`}>{verdict.ok ? `Exact ! +${holePts(hi)} pts` : 'Raté…'}</span>
          {verdict.ok ? <button type="button" className="qs-btn is-ghost" onClick={() => force(false)}>Compter faux</button> : <button type="button" className="qs-btn is-ghost" onClick={() => force(true)}>Compter juste</button>}
          <button type="button" className="qs-btn is-gold" onClick={resume}>▶ Reprendre la musique</button>
        </div>
      )}
      {phase === 'playing' && <div className="qs-host"><span className="qs-host-tip">🎵 {fmtT(p.time)}</span><button type="button" className="qs-btn is-ghost" onClick={() => { p.pause(); setPhase('end'); }}>Terminer</button></div>}
      {phase === 'end' && <Done points={total} onDone={onDone} />}
    </div>
  );
}

// ——— Éditeur : paroles + synchronisation au tap ———
export function LyricsField({ value, onChange, root }) {
  const lines = value || [];
  const raw = lines.map((l) => l.text).join('\n');
  const p = useMediaPlayer(root.media);
  const area = useRef(null);
  const [sync, setSync] = useState(null); // index de la prochaine ligne à taper
  const [find, setFind] = useState(null); // { q, busy, list, err }
  const holesCount = lines.filter((l) => splitHole(l.text)).length;
  const setRaw = (txt) => {
    const lrc = parseLRC(txt);
    if (lrc.length >= 2) { onChange(lrc); return; } // paroles LRC collées : timings repris tels quels
    const texts = txt.split('\n');
    onChange(texts.map((t, k) => ({ text: t, t: lines[k] ? lines[k].t : '', stop: lines[k] ? lines[k].stop : '' })));
  };
  const setLine = (k, patch) => onChange(lines.map((l, j) => (j === k ? { ...l, ...patch } : l)));
  // Une seule frappe par ligne : la coupure des trous est estimée (ajustable à la main ensuite)
  const mark = useCallback(() => {
    if (sync == null) return;
    const t = Math.round(p.getTime() * 10) / 10;
    let i = sync; while (lines[i] && !lines[i].text) i += 1;
    if (!lines[i]) { setSync(null); p.pause(); return; }
    onChange(lines.map((x, j) => (j === i ? { ...x, t, stop: '' } : x)));
    let n = i + 1; while (lines[n] && !lines[n].text) n += 1;
    if (n >= lines.length) { setSync(null); p.pause(); } else setSync(n);
  }, [sync, lines, p, onChange]);
  useEffect(() => {
    if (sync == null) return undefined;
    const k = (e) => { if (e.code === 'Space') { e.preventDefault(); mark(); } };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [sync, mark]);
  const startSync = () => { p.seek(Number(root.start) || 0); p.play(); setSync(0); };
  const test = (k) => { const l = lines[k]; const stop = holeStop(lines, k); p.seek(Math.max(0, Number(l.t) - 1.5)); p.play(); setTimeout(() => p.pause(), (stop - Number(l.t) + 1.5) * 1000); };
  const shift = (d) => onChange(lines.map((l) => ({ ...l, t: l.t === '' || l.t == null ? l.t : Math.max(0, Math.round((Number(l.t) + d) * 10) / 10), stop: Number(l.stop) > 0 ? Math.max(0, Math.round((Number(l.stop) + d) * 10) / 10) : l.stop })));
  // Sélectionne des mots dans le texte puis « Faire un trou » : les crochets sont posés pour toi
  const makeHole = () => {
    const el = area.current; if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    if (a === b) return;
    const txt = raw; const sel = txt.slice(a, b);
    if (/[\n[\]]/.test(sel)) return;
    setRaw(`${txt.slice(0, a)}[${sel.trim()}]${txt.slice(b)}`);
  };
  const search = async (q) => {
    setFind({ q, busy: true, list: [] });
    try {
      const r = await fetch(`https://lrclib.net/api/search?q=${encodeURIComponent(q)}`);
      const list = (await r.json()).filter((x) => x.syncedLyrics).slice(0, 12);
      setFind({ q, busy: false, list, err: list.length ? '' : 'Aucune version synchronisée trouvée : essaie un autre titre ou utilise le tap.' });
    } catch (e) { setFind({ q, busy: false, list: [], err: 'Recherche impossible pour le moment.' }); }
  };
  const pick = (x) => { onChange(parseLRC(x.syncedLyrics)); setFind(null); };
  return (
    <div className="qse-lyrics">
      {p.node}
      <div className="qse-lyrics-bar">
        {!find ? <button type="button" className="qs-btn is-gold" onClick={() => search(root.title || '')}>🔎 Trouver les paroles déjà synchronisées</button> : (
          <div className="qse-lrc-find">
            <form onSubmit={(e) => { e.preventDefault(); search(find.q); }}><input value={find.q} onChange={(e) => setFind({ ...find, q: e.target.value })} placeholder="Titre (et artiste)" /><button type="submit" className="qs-btn is-gold">Chercher</button><button type="button" className="qs-btn is-ghost" onClick={() => setFind(null)}>Fermer</button></form>
            {find.busy ? <em>Recherche…</em> : find.err ? <em>{find.err}</em> : (
              <ul>{find.list.map((x) => <li key={x.id}><button type="button" onClick={() => pick(x)}><b>{x.trackName}</b> · {x.artistName}{x.duration ? ` · ${fmtT(x.duration).replace(/\.\d$/, '')}` : ''}</button></li>)}</ul>
            )}
          </div>
        )}
      </div>
      <p className="qse-note">Les paroles arrivent avec leurs timings. S’ils sont décalés par rapport à ta vidéo, utilise « −0,5 s / +0,5 s ». Tu peux aussi coller directement un fichier .lrc dans le cadre.</p>
      <textarea ref={area} rows={8} value={raw} onChange={(e) => setRaw(e.target.value)} placeholder="Colle les paroles ici (ou un .lrc)…" />
      <div className="qse-lyrics-bar">
        <span>{lines.filter((l) => l.text).length} lignes · {holesCount} trou{holesCount > 1 ? 's' : ''}</span>
        <button type="button" className="qs-btn is-ghost" onClick={makeHole} title="Sélectionne des mots dans le texte, puis clique">[ ] Faire un trou avec la sélection</button>
        {lines.some((l) => Number(l.t) > 0) && <><button type="button" className="qs-btn is-ghost" onClick={() => shift(-0.5)}>−0,5 s</button><button type="button" className="qs-btn is-ghost" onClick={() => shift(0.5)}>+0,5 s</button></>}
      </div>
      <div className="qse-lyrics-bar">
        {!root.media ? <em>Ajoute d’abord la musique au-dessus pour synchroniser au tap.</em> : sync == null ? (
          <button type="button" className="qs-btn is-gold" disabled={!p.ready || !lines.length} onClick={startSync}>⏱ Synchroniser au tap</button>
        ) : (
          <>
            <button type="button" className="qs-btn is-green is-big" onClick={mark}>TAP (Espace)</button>
            <span className="qse-sync-next">Début de la ligne {sync + 1} : « {lines[sync] && lines[sync].text.slice(0, 60)} »</span>
            <button type="button" className="qs-btn is-ghost" onClick={() => { setSync(null); p.pause(); }}>Arrêter</button>
            <span className="qse-sync-t">{fmtT(p.time)}</span>
          </>
        )}
      </div>
      <p className="qse-note">Sans paroles synchronisées : lance « Synchroniser au tap » et tape Espace une fois au début de chaque ligne. La coupure sur les trous se cale toute seule ; écoute-la avec ▶ et corrige-la au besoin.</p>
      <div className="qse-lyrics-table">
        {lines.map((l, k) => l.text ? (
          <div key={k} className={`qse-lrow${splitHole(l.text) ? ' has-hole' : ''}${sync === k ? ' is-sync' : ''}`}>
            <i>{k + 1}</i>
            <span className="qse-ltext">{l.text}</span>
            <input value={l.t === '' || l.t == null ? '' : fmtT(Number(l.t))} placeholder="début" onChange={(e) => setLine(k, { t: parseT(e.target.value) })} title="Début de la ligne (m:ss.s)" />
            {splitHole(l.text) ? <input value={Number(l.stop) > 0 ? fmtT(Number(l.stop)) : ''} placeholder={l.t === '' || l.t == null ? 'trou' : `auto ${fmtT(holeStop(lines, k))}`} onChange={(e) => setLine(k, { stop: parseT(e.target.value) })} title="Coupure de la musique (vide = automatique)" /> : <span />}
            {splitHole(l.text) && root.media ? <button type="button" onClick={() => test(k)} title="Écouter jusqu’à la coupure">▶</button> : <span />}
          </div>
        ) : null)}
      </div>
    </div>
  );
}

export const validateKaraoke = (d) => {
  const w = []; const lines = (d.lines || []).filter((l) => l.text);
  if (!d.media) w.push('Ajoute la musique (lien YouTube ou fichier)');
  if (!lines.length) w.push('Colle les paroles');
  const holes = lines.filter((l) => splitHole(l.text));
  if (!holes.length && lines.length) w.push('Mets au moins un trou entre [crochets]');
  if (holes.length > 5) w.push(`${holes.length} trous : 5 maximum conseillés (au-delà, chaque trou vaut 6 pts)`);
  lines.forEach((l, k) => {
    if ((String(l.text).match(/\[/g) || []).length > 1) w.push(`Ligne ${k + 1} : un seul trou par ligne`);
    if (l.t === '' || l.t == null || Number.isNaN(Number(l.t))) w.push(`Ligne ${k + 1} : pas de temps de début (synchronise)`);
  });
  return w;
};
